<template>
    <div id="keyboardShortcuts">
        <div id="shortcutsSidebarControls">
            <div id="closeShortcutsSidebar">
                <DsIconButton name="x" class="close-icon" @click="closeSidebar" />
            </div>
            <span id="shortcutsSidebarTitle">
                {{ $t('funnel.shortcuts.title') }}
            </span>
            <div
                id="platformMenuButton"
                v-click-outside="closePlatformMenu"
                @click="togglePlatformMenu"
            >
                <img v-if="showMacKeys" id="platformIcon" src="/resources/png/ks-mac-icon@2x.png" />

                <img v-else id="platformIcon" src="/resources/png/ks-windows-icon@2x.png" />

                <img
                    id="platformMenuChevron"
                    src="/resources/svg/ic-chevron-down.svg"
                />
            </div>
            <div v-show="showPlatformMenu" id="platformMenu">
                <div class="platform-menu-item" @click="showMacShortcuts">
                    <img src="/resources/png/ks-mac-icon@2x.png" />
                    <span class="platform-menu-item-text">
                        {{ $t('funnel.shortcuts.menu.mac') }}
                    </span>
                </div>
                <div class="platform-menu-item" @click="showPcShortcuts">
                    <img src="/resources/png/ks-windows-icon@2x.png" />
                    <span class="platform-menu-item-text">
                        {{ $t('funnel.shortcuts.menu.windows') }}
                    </span>
                </div>
            </div>
        </div>

        <div class="search-container">
            <DsInputField
                id="shortcutsSearch"
                v-model="criteria"
                :placeholder="$t('funnel.shortcuts.search.value')"
            />
        </div>

        <div id="shortcutsContainer">
            <div v-if="!isHidden('select')" class="shortcut-entry">
                <div class="shortcut-description">
                    {{ shortcuts['select'].description }}
                </div>
                <div class="shortcut-key-mapping">
                    <span class="shortcut-key">Shift</span> + {{ $t('funnel.shortcuts.instruction.click') }}
                </div>
            </div>
            <div v-if="!isHidden('selectMultiple')" class="shortcut-entry">
                <div class="shortcut-description">
                    {{ shortcuts['selectMultiple'].description }}
                </div>
                <div class="shortcut-key-mapping">
                    <span class="shortcut-key">Shift</span> + {{ $t('funnel.shortcuts.instruction.draghighlight') }}
                </div>
            </div>
            <div v-if="!isHidden('delete')" class="shortcut-entry">
                <div class="shortcut-description">
                    {{ shortcuts['delete'].description }}
                </div>
                <div class="shortcut-key-mapping">
                    <span class="shortcut-key">Delete</span>
                </div>
            </div>
            <div v-if="!isHidden('duplicate')" class="shortcut-entry">
                <div class="shortcut-description">
                    {{ shortcuts['duplicate'].description }}
                </div>

                <div class="shortcut-key-mapping">
                    <template v-if="showMacKeys">
                        <span class="shortcut-key">Control</span> + {{ $t('funnel.shortcuts.instruction.drag') }}
                    </template>
                    <template v-else>
                        <span class="shortcut-key">Ctrl</span> + {{ $t('funnel.shortcuts.instruction.drag') }}
                    </template>
                </div>
            </div>
            <div v-if="!isHidden('findElement') && showFind" class="shortcut-entry">
                <div class="shortcut-description">
                    {{ shortcuts['findElement'].description }}
                </div>
                <div class="shortcut-key-mapping">
                    <template v-if="showMacKeys">
                        <span class="shortcut-key">⌘</span> + <span class="shortcut-key">j</span>
                    </template>
                    <template v-else>
                        <span class="shortcut-key">Ctrl</span> + <span class="shortcut-key">j</span>
                    </template>
                </div>
            </div>
            <div v-if="!isHidden('openClose')" class="shortcut-entry">
                <div class="shortcut-description">
                    {{ shortcuts['openClose'].description }}
                </div>
                <div class="shortcut-key-mapping">
                    <span class="shortcut-key">Shift</span> + <span class="shortcut-key">?</span>
                </div>
            </div>
            <div v-if="!isHidden('zoomOut')" class="shortcut-entry">
                <div class="shortcut-description">
                    {{ shortcuts['zoomOut'].description }}
                </div>
                <div class="shortcut-key-mapping">
                    <template v-if="showMacKeys">
                        <span class="shortcut-key">⌘</span> + <span class="shortcut-key">-</span>
                    </template>
                    <template v-else>
                        <span class="shortcut-key">Ctrl</span> + <span class="shortcut-key">-</span>
                    </template>
                </div>
            </div>
            <div v-if="!isHidden('zoomIn')" class="shortcut-entry">
                <div class="shortcut-description">
                    {{ shortcuts['zoomIn'].description }}
                </div>
                <div class="shortcut-key-mapping">
                    <template v-if="showMacKeys">
                        <span class="shortcut-key">⌘</span> + <span class="shortcut-key">+</span>
                    </template>
                    <template v-else>
                        <span class="shortcut-key">Ctrl</span> + <span class="shortcut-key">+</span>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const PLATFORMS = {
    Mac: 'MAC',
    Windows: 'PC',
};

export default {
    props: {
        showFind: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['close-sidebar'],
    data() {
        return {
            showPlatformMenu: false,
            platform: '',
            criteria: '',
        };
    },
    computed: {
        isMac() {
            return (navigator?.userAgent || '').toUpperCase().includes('MAC');
        },

        showMacKeys() {
            return this.platform === PLATFORMS.Mac;
        },

        isHidden() {
            return (shortcutKey) => {
                const shortcut = this.shortcuts[shortcutKey];

                if (this.criteria) {
                    const hiddenBySearch = !shortcut.description.toUpperCase().includes(this.criteria.toUpperCase());

                    if (hiddenBySearch) return true;
                }

                return false;
            };
        },

        shortcuts() {
            return {
                select: {
                    description: this.$t('funnel.shortcuts.label.select'),
                },
                selectMultiple: {
                    description: this.$t('funnel.shortcuts.label.select.multiple'),
                },
                delete: {
                    description: this.$t('funnel.shortcuts.label.delete'),
                },
                duplicate: {
                    description: this.$t('funnel.shortcuts.label.duplicate'),
                },
                openClose: {
                    description: this.$t('funnel.shortcuts.label.open.close'),
                },
                findElement: {
                    description: this.$t('funnel.shortcuts.label.findElement'),
                },
                zoomOut: {
                    description: this.$t('funnel.shortcuts.label.zoom.out'),
                    requiresZoomEnabled: true,
                },
                zoomIn: {
                    description: this.$t('funnel.shortcuts.label.zoom.in'),
                    requiresZoomEnabled: true,
                },
            };
        },
    },
    async mounted() {
        this.platform = this.isMac ? PLATFORMS.Mac : PLATFORMS.Windows;
    },
    methods: {
        closeSidebar() {
            this.$emit('close-sidebar');
        },
        togglePlatformMenu() {
            this.showPlatformMenu = !this.showPlatformMenu;
        },
        closePlatformMenu() {
            this.showPlatformMenu = false;
        },
        showMacShortcuts() {
            this.platform = PLATFORMS.Mac;
            this.closePlatformMenu();
        },
        showPcShortcuts() {
            this.platform = PLATFORMS.Windows;
            this.closePlatformMenu();
        },
    },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
#keyboardShortcuts {
    width: 320px;
    height: 100%;
    border: solid 1px $color-gray-200;
    background-color: $color-paper;

    animation-name: slideInRight;
    transition: all 0.5s ease-out;
    animation-duration: 0.5s;
    animation-fill-mode: both;

    position: fixed;
    right: 0;
    top: 0;
    z-index: 50;
    white-space: nowrap;
    --input-margin-bottom: 0;
}

#shortcutsSidebarControls {
    display: grid;
    grid-template-areas: "closeshortcutstraypanel shortcutstraypaneltitle shortcutstraymenubutton";
    grid-template-columns: 1fr 4fr 1fr;
}

#closeShortcutsSidebar {
    grid-area: closeshortcutstraypanel;
    cursor: pointer;
    margin-top: $spacing-100;
    margin-left: $spacing-050;
}

#closeShortcutsSidebar .close-icon {
    width: 20px;
    height: 20px;
}

#shortcutsSidebarTitle {
    grid-area: shortcutstraypaneltitle;
    font-size: $font-size-md;
    font-stretch: normal;
    font-style: normal;
    line-height: $line-height-md;
    letter-spacing: normal;
    color: #242433;
    margin: $spacing-200 $spacing-200 0 0;
}

#platformMenuButton {
    grid-area: shortcutstraymenubutton;
    cursor: pointer;
    margin: $spacing-200 $spacing-200 0 0;
}

#platformIcon {
    width: 15px;
    height: 16px;
}

#platformMenuChevron {
    width: 10px;
    height: 5px;
}

#platformMenu {
    position: absolute;
    width: 189px;
    padding-top: $spacing-100;
    padding-bottom: $spacing-100;
    margin-top: 45px;
    margin-left: 110px;
    border-radius: 8px;
    box-shadow: $elevation-z8;
    background-color: $color-paper;
    z-index: 1;
}

.platform-menu-item {
    background-color: $color-paper;
    padding-top: $spacing-150;
    padding-bottom: $spacing-150;
    padding-left: $spacing-300;
    cursor: pointer;
}

.platform-menu-item:hover {
    background-color: $color-gray-100;
}

.platform-menu-item img {
    width: 24px;
    height: 24px;
}

.platform-menu-item-text {
    height: 24px;
    margin-left: $spacing-200;
    font-size: $font-size-md;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: $line-height-lg;
    letter-spacing: normal;
    color: #242433;
}

.search-container {
    margin: $spacing-200;
}

.shortcut-entry {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: $font-size-xs;
    padding: $spacing-050 $spacing-250;
    border-bottom: solid 1px $color-gray-200;
}

.shortcut-description {
    grid-area: description;
    line-height: $line-height-xl;
    margin-top: auto;
    margin-bottom: auto;
}

.shortcut-key {
    border-radius: 4px;
    box-shadow: $elevation-z1;
    background-color: $color-gray-050;
    padding: 5px 8px 5px 8px;
}
</style>
